import type { SpeakOptions, SpeakSource } from '../contracts';
import type { PcmStream } from './pcm';
import { fetchAudio, collectAudio } from './audio-transport';
import type { PlaybackProfile } from '../lane-timing';
import { audioByteLimit } from '../../../src/speech/tts/read-aloud-policy.js';
export class PlaybackError extends Error {
  constructor(public code: string) {
    super(code);
  }
}
export type AudioRequestResult = {
  audio: AudioBuffer | PcmStream;
  profile: PlaybackProfile | null;
  playbackId: string | null;
};
export async function audioRequest(
  fetchImpl: typeof fetch,
  context: AudioContext,
  source: SpeakSource,
  sentence: number,
  options: SpeakOptions,
  signal: AbortSignal,
) {
  signal.throwIfAborted();
  const { response, chunks, cancel } = await fetchAudio(
    fetchImpl,
    JSON.stringify({ source, sentence, rate: options.rate }),
    signal,
    audioByteLimit(source),
  );
  signal.throwIfAborted();
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new PlaybackError(
      error.reason === 'payment_required' ? 'payment_required' : (error.error ?? 'tts_unavailable'),
    );
  }
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  const profile = parsePlaybackProfile(response.headers.get('x-poneva-voice-profile'));
  const playbackId = response.headers.get('x-poneva-playback-id');
  // voice-structural: the provider audio media type selects the PCM decoder
  if (contentType.startsWith('audio/pcm')) {
    const rate = Number(response.headers.get('x-poneva-sample-rate'));
    if (!Number.isInteger(rate) || rate < 8_000 || rate > 48_000) {
      cancel();
      throw new PlaybackError('tts_format_unsupported');
    }
    const pcm: PcmStream = {
      kind: 'pcm',
      sampleRate: rate,
      chunks,
    };
    return { audio: pcm, profile, playbackId };
  }
  const bytes = await collectAudio(chunks);
  signal.throwIfAborted();
  const clip = await context.decodeAudioData(bytes);
  signal.throwIfAborted();
  return { audio: clip, profile, playbackId };
}

function parsePlaybackProfile(value: string | null): PlaybackProfile | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Record<string, unknown>;
    if (
      typeof parsed.provider !== 'string' ||
      typeof parsed.model !== 'string' ||
      typeof parsed.voiceId !== 'string' ||
      typeof parsed.name !== 'string'
    )
      return null;
    return {
      provider: parsed.provider,
      model: parsed.model,
      voiceId: parsed.voiceId,
      name: parsed.name,
    };
  } catch {
    return null;
  }
}
